import { performRequestSecond, performRequestThird } from 'common/apiHandler';
import { default as apiEndPoints } from 'common/apiEndPoints';
import { getTenantDetails } from 'utils/utils';

// Normalizes a date-like value to the format: YYYY-MM-DDT00:00:00+00:00 (UTC midnight)
const normalizeDateToUtcMidnight = (value) => {
  try {
    if (!value) return value;

    const buildFromDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}T00:00:00+00:00`;
    };

    if (typeof value === 'string') {
      const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        return `${dateMatch[1]}T00:00:00+00:00`;
      }
      const parsed = new Date(value);
      if (!Number.isNaN(parsed?.getTime())) {
        return buildFromDate(parsed);
      }
      return value;
    }

    if (value instanceof Date) {
      if (!Number.isNaN(value.getTime())) {
        return buildFromDate(value);
      }
      return value;
    }

    if (typeof value === 'number') {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed?.getTime())) {
        return buildFromDate(parsed);
      }
      return value;
    }

    return value;
  } catch (_err) {
    return value;
  }
};

export const diff_hours = (duration) => {
  try {
    let v = 0.1;
    var diff = duration / 1000;
    var minutes = Math.ceil(diff / 60);
    let val = Math.ceil(minutes / 6);
    let hoursInSix = v * val;
    if (hoursInSix) {
      return hoursInSix.toString();
    } else {
      return '0.1';
    }
  } catch (err) {
    return '0.1';
  }
};

export const dhm = (t) => {
  let duration = t;
  //let milliseconds = Math.floor((duration % 1000) / 100);
  let seconds = Math.floor((duration / 1000) % 60);
  let minutes = Math.floor((duration / (1000 * 60)) % 60);
  let hours = Math.floor((duration / (1000 * 60 * 60)) % 24);
  let days = Math.floor(duration / (1000 * 60 * 60 * 24));

  hours = hours < 10 ? '0' + hours : hours;
  minutes = minutes < 10 ? '0' + minutes : minutes;
  seconds = seconds < 10 ? '0' + seconds : seconds;

  hours = days > 0 ? days * 24 + parseInt(hours) : hours;

  return hours + ':' + minutes + ':' + seconds; /* + "." + milliseconds; */
};

export const createTimeEntry = (data, is_lead = false, bulk_time_entry = false) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.createTimeEntry}?tenant_id=${tenantId}&tenant_sk=${tenantSk}${is_lead ? `&is_lead=${is_lead}` : ''}${
    bulk_time_entry ? `&bulk_time_entry=${bulk_time_entry}` : ''
  }`;
  // Ensure each entry's time_entry_date is normalized to UTC midnight format

  const normalizedData = (() => {
    try {
      if (data && Array.isArray(data.time_entries)) {
        const normalizedEntries = data.time_entries.map((entry) => {
          if (!entry || typeof entry !== 'object') return entry;
          return {
            ...entry,
            time_entry_date: normalizeDateToUtcMidnight(entry?.time_entry_date),
          };
        });
        return { ...data, time_entries: normalizedEntries };
      }
      return data;
    } catch (_err) {
      return data;
    }
  })();

  return performRequestSecond(apiEndPoints.methodType.POST, url, normalizedData);
};

export const updateTimeEntry = (data, is_lead = false, timeEntryData = '', login = true, excludeFromLoading = false) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.updateTimeEntry}?tenant_id=${tenantId}&tenant_sk=${tenantSk}${is_lead ? `&is_lead=${is_lead}` : ''}`;

  const normalizedData = (() => {
    try {
      if (data && typeof data === 'object') {
        // handle single object
        return {
          ...data,
          time_entry_date: normalizeDateToUtcMidnight(data?.time_entry_date),
        };
      }
      return data;
    } catch (_err) {
      return data;
    }
  })();
  return performRequestSecond(apiEndPoints.methodType.PUT, url, normalizedData, timeEntryData, login, excludeFromLoading);
};

export const listTimeEntries = (
  data,
  is_automated = true,
  pageLimit = 10,
  pagination_token = '',
  pagination_direction = 'forward',
  is_full_list = false,
  excludeFromLoading = false
) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.listTimeEntries}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&is_automated=${is_automated}&limit=${pageLimit}&pagination_token=${pagination_token}&pagination_direction=${pagination_direction}&is_full_list=${is_full_list}`;
  return performRequestSecond(apiEndPoints.methodType.POST, url, data, null, true, excludeFromLoading);
};

export const getTimeEntry = (time_entry_sk) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.getTimeEntry}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&time_entry_sk=${time_entry_sk}`;
  return performRequestSecond(apiEndPoints.methodType.GET, url);
};

export const deleteTimeEntry = (time_entry_sk, is_lead = false) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.deleteTimeEntry}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&time_entry_sk=${time_entry_sk}${
    is_lead ? `&is_lead=${is_lead}` : ''
  }`;
  return performRequestSecond(apiEndPoints.methodType.DELETE, url);
};

export const createActivityType = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.createActivityType}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`;
  return performRequestSecond(apiEndPoints.methodType.POST, url, data);
};

export const updateActivityType = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.updateActivityType}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`;
  return performRequestSecond(apiEndPoints.methodType.PUT, url, data);
};

export const getActivityType = (sk, pk) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.getActivityTypes}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&sk=${sk}&pk=${pk}`;
  return performRequestSecond(apiEndPoints.methodType.GET, url);
};

export const deleteActivityType = (sk, pk) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.deleteActivityTypes}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&sk=${sk}&pk=${pk}`;
  return performRequestSecond(apiEndPoints.methodType.DELETE, url);
};

export const archiveActivityType = (sk, pk, action) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.deleteActivityTypes}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&sk=${sk}&pk=${pk}&action=${action}`;
  return performRequestSecond(apiEndPoints.methodType.DELETE, url);
};

export const listActivityTypes = (
  next = '',
  limit = 10,
  pagination_token = '',
  pagination_direction = 'forward',
  filters = {},
  excludeFromLoading = false
) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const { practice_area_ids, category, status } = filters;
  let url = `${
    apiEndPoints.listActivityTypes
  }?tenant_id=${tenantId}&tenant_sk=${tenantSk}&next=${next}&limit=${limit}&pagination_token=${pagination_token}&pagination_direction=${pagination_direction}${
    practice_area_ids ? `&practice_area_ids=${practice_area_ids}` : ''
  }${category ? `&category=${category}` : ''}${status ? `&status=${status}` : ''}`;
  return performRequestSecond(apiEndPoints.methodType.POST, url, null, null, true, excludeFromLoading);
};

export const getBillingCode = (module_name, excludeFromLoading = true) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.listModuleType}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&module_name=${module_name}`;
  return performRequestThird(apiEndPoints.methodType.GET, url, null, null, true, excludeFromLoading);
};

export const getSummaryOfTimeEntries = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.getSummaryOfTimeEntries}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`;
  return performRequestSecond(apiEndPoints.methodType.POST, url, data);
};

export const getUserHourRate = (case_id = '', team_member_firm_user_id = '') => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.getHourRate}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&case_id=${case_id}&team_member_firm_user_id=${team_member_firm_user_id}`;
  return performRequestSecond(apiEndPoints.methodType.GET, url);
};

export const getTimeTracker = () => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.timeTracker}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`;
  return performRequestThird(apiEndPoints.methodType.GET, url);
};

// Track billable hours (My Time tracker)
// Wrapper around QA endpoint: /track-billable-hours
// startDate and endDate should be ISO strings with timezone offset (e.g. 2025-07-01T00:00:00+05:30)
export const trackBillableHours = (firm_user_id, startDate, endDate, excludeFromLoading = false) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const params = new URLSearchParams();
  params.set('tenant_id', tenantId);
  params.set('tenant_sk', tenantSk);
  if (firm_user_id) params.set('firm_user_id', firm_user_id);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  const url = `${apiEndPoints.trackBillableHours}?${params.toString()}`;
  return performRequestSecond(apiEndPoints.methodType.GET, url, {}, '', true, excludeFromLoading);
};
